import {
  bgColors,
  colors,
  getTextColorFromBackgroundColor,
} from 'components/colors';
import { OldCustomField } from 'types/oldCustomField';
import { Color } from 'types/colors';
import { CustomFieldDefinition } from 'types/customField';
import { TFunction } from 'i18next';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

function buildTagHtmlMarkup(
  label: string,
  color: Color | TagColorWithIntensity,
) {
  const baseStyle =
    'font-weight:500;padding:0.125rem 0.625rem 0.125rem 0.625rem;border-radius:9999px;--tw-border-opacity:1';
  const bgStyle = bgColors(color).styles;
  const textStyle = getTextColorFromBackgroundColor(color).styles;
  return `<span style="${baseStyle};${bgStyle};${textStyle}">${label}</span>`;
}

function getCustomFieldValueAsText(
  customField: OldCustomField,
  definition: CustomFieldDefinition,
  t: TFunction<['cahierJournal' | 'common']>,
) {
  switch (definition.type) {
    case 'text': {
      const customFieldValue = (customField.value as string) || '';
      // Froala put systematically a <p> tag around the text
      // We remove it to avoid having to have the label and the text on the same line
      if (
        customFieldValue.startsWith('<p>') &&
        customFieldValue.endsWith('</p>')
      ) {
        return customFieldValue.substring(3, customFieldValue.length - 4);
      }
      return customFieldValue;
    }

    case 'select': {
      const options = definition?.options;
      const customFieldValue = (customField.value as string[]) || [];
      return customFieldValue
        .map(fieldValue => {
          const option = options?.find(opt => opt.id === fieldValue);
          const noTitleLabel: string = t('cahierJournal:labels.noTitle');
          return buildTagHtmlMarkup(
            option?.value || noTitleLabel.toLowerCase(),
            option?.color || 'gris-moderate',
          );
        })
        .join('');
    }

    case 'multiselect':
      return (customField.items || []).map(i => i.value).join(', ');

    case 'number':
      return customField.value?.toString() || '';

    case 'phone': {
      const phoneValue = customField.value as string;
      return phoneValue || '';
    }

    case 'bool': {
      const boolValue = customField.value as boolean;
      return boolValue
        ? t('common:customFields.bool.yes')
        : t('common:customFields.bool.no');
    }

    case 'filesAndMedia': {
      const files = (customField.value as any[]) || [];
      return files.length > 0
        ? t('common:customFields.filesAndMedia.xFiles', { count: files.length })
        : '';
    }
  }
}

export function displayCustomFieldsHtml(
  customFields: OldCustomField[],
  customFieldDefinitions: CustomFieldDefinition[],
  t: TFunction<['cahierJournal' | 'common']>,
): string {
  const content = customFields.reduce<string>((acc, customField) => {
    const customFieldDefinition = customFieldDefinitions.find(
      def => def.id === customField.id,
    );
    if (!customFieldDefinition) {
      return acc;
    }

    const customFieldTextValue = getCustomFieldValueAsText(
      customField,
      customFieldDefinition,
      t,
    );
    if (!customFieldTextValue) {
      return acc;
    }
    const customFieldName =
      (customField.items || []).length > 1
        ? customFieldDefinition.pluralName
        : customFieldDefinition.name;
    return `
    ${acc}<div><strong style="color: ${colors['gray-400']}">${customFieldName} :&nbsp;</strong>${customFieldTextValue}</div>
`;
  }, '');

  if (!content) {
    return '';
  }

  return `<div style="margin-bottom: 6px">${content}</div>`;
}
